import React from 'react';
import PropTypes from 'prop-types';

import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

export default function SingleViewDialog({ open, onClose, data, type }) {
  if (!data) return null;

  // Render criteria if present
  const renderCriteria = (criteria) => {
    if (!Array.isArray(criteria)) return null;
    return (
      <Stack spacing={1} sx={{ mt: 1 }}>
        {criteria.map((c, idx) => (
          <Stack key={idx} spacing={0.5} sx={{ pl: 2, borderLeft: '2px solid #eee' }}>
            <Typography variant="subtitle2">Criteria {idx + 1}</Typography>
            {c.lookbackPeriod && (
              <Typography variant="body2">
                Lookback: {c.lookbackPeriod.title} ({c.lookbackPeriod.from} - {c.lookbackPeriod.to})
              </Typography>
            )}
            {c.exclude && <Typography variant="body2">Exclude: {c.exclude}</Typography>}
            {Array.isArray(c.conditions) && c.conditions.length > 0 && (
              <Stack spacing={0.5}>
                <Typography variant="body2">Conditions:</Typography>
                {c.conditions.map((cond, i) => (
                  <Typography variant="body2" key={i} sx={{ pl: 2 }}>
                    - {cond.metric} {cond.operator} {cond.value}
                  </Typography>
                ))}
              </Stack>
            )}
            {c.action && (
              <Typography variant="body2">
                Action: {c.action.actionType} {c.action.value ? `(${c.action.value})` : ''}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {type === 'execution' ? 'Execution Log Details' : 'Automation Log Details'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="h6">{data.title}</Typography>
          {data.description && <Typography variant="body2">{data.description}</Typography>}
          <Divider />
          <Stack direction="row" spacing={2}>
            <Typography variant="body2">
              Status: <b>{data.status || '--'}</b>
            </Typography>
            <Typography variant="body2">
              Execution Type: <b>{data.executionType || '--'}</b>
            </Typography>
          </Stack>
          <Typography variant="body2">
            Created At: {data.createdAt ? new Date(data.createdAt).toLocaleString() : '--'}
          </Typography>
          {data.completedAt && (
            <Typography variant="body2">
              Completed At: {data.completedAt ? new Date(data.completedAt).toLocaleString() : '--'}
            </Typography>
          )}
          {data.error && (
            <Typography variant="body2" color="error">
              Error:{' '}
              {typeof data.error === 'string'
                ? data.error
                : data.error.message || JSON.stringify(data.error)}
            </Typography>
          )}
          {renderCriteria(data.criteria)}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SingleViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  type: PropTypes.oneOf(['automation', 'execution']),
};
